import { css, cx } from '@linaria/core'
import {
  BodyText,
  Button,
  ButtonGroup,
  elFlex1,
  elHFull,
  elMt8,
  FlexContainer,
  FormLayout,
  InputError,
  InputWrapFull,
  Label,
  Select,
} from '@reapit/elements'
import { DialogWrapper } from '@reapitform/common-ui'
import { sendForActionSchema, TSendForAction, useSendForAction } from '../../api/documents/actions/send-for-signing'
import React from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

const deadlineOptions = [
  { title: '24 hours', value: 24 },
  { title: '48 hours', value: 48 },
  { title: '72 hours', value: 72 },
  { title: '7 days', value: 168 },
  { title: '30 days', value: 720 },
  { title: '90 days', value: 2160 },
]

interface ISendForSigningProps {
  isOpen?: boolean
  onClose?: () => void
  onFinish?: (agreementIds: string[]) => void
  agreementId?: string[]
}

export const SendForSigningModal: React.FC<ISendForSigningProps> = ({
  isOpen = false,
  onClose,
  onFinish,
  agreementId,
}) => {
  const { sendForSigning, isLoading: isSending } = useSendForAction()
  const {
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm<TSendForAction>({
    defaultValues: {
      iDs: undefined,
      linkExpiryHours: '',
    },
    resolver: yupResolver(sendForActionSchema),
  })

  React.useEffect(() => {
    if (!agreementId) return
    setValue('iDs', agreementId)
  }, [agreementId])

  if (!agreementId || !agreementId.length) return

  const onSubmit = async (data: TSendForAction) => {
    await sendForSigning(data)
    onFinish?.(agreementId)
    onClose?.()
    reset()
  }

  const handleClose = () => {
    onClose?.()
    reset()
  }

  return (
    <DialogWrapper handlerModalClose={handleClose} isModalOpen={isOpen} title="Send for Signing">
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexContainer isFlexColumn className={cx(elHFull, elFlex1)}>
          <BodyText hasSectionMargin>
            You are about to send this agreement for signing. The tenants will receive a link to their agreement via SMS
            and email.
          </BodyText>
          <FormLayout>
            <InputWrapFull>
              <Label>Link Expiry *</Label>
              <Select
                className={cx(
                  errors.linkExpiryHours?.message &&
                    css`
                      background-color: var(--color-red-100);
                      border: 1px solid var(--intent-danger);
                    `,
                )}
                {...register('linkExpiryHours')}
              >
                <option value="">Select</option>
                {deadlineOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.title}
                  </option>
                ))}
              </Select>
              {errors.linkExpiryHours?.message && <InputError message={errors.linkExpiryHours.message} />}
            </InputWrapFull>
          </FormLayout>
          <ButtonGroup alignment="right" className={cx(elMt8)}>
            <Button intent="default" disabled={isSending} onClick={handleClose}>
              Cancel
            </Button>
            <Button loading={isSending} disabled={isSending} intent="primary" type="submit">
              Send
            </Button>
          </ButtonGroup>
        </FlexContainer>
      </form>
    </DialogWrapper>
  )
}
